import React, {
  forwardRef,
  useState,
  useRef,
  useLayoutEffect,
  useImperativeHandle,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import type { RefAttributes } from 'react';
import clsx from 'clsx';
import { isNil, throttle, debounce } from 'lodash';

import { rootClassname, viewportDraggingClassname, focusSinkClassname } from './style';
import {
  useGridDimensions,
  useCalculatedColumns,
  useViewportColumns,
  useViewportRows,
  useLatestFunc,
  useMouse,
  useSelection,
  usePaste,
  useLatest,
  useSorting,
} from './hooks';
import HeaderRow from './HeaderRow';
import Row from './Row';
import SummaryRow from './SummaryRow';
import {
  onEditorNavigation,
  getNextSelectedCellPosition,
  canExitGrid,
  isCtrlKeyHeldDown,
  isDefaultCellInput,
} from './utils';

import type {
  CalculatedColumn,
  Position,
  SelectedCellProps,
  EditCellProps,
  SortDirection,
  ISelectRange,
  DataGridProps,
  DataGridRef,
  RowsChangeData,
  IRange,
  Column,
  FilterOptions,
  RowChangeType,
} from './types';
import { RangeMode, ColumnMetric } from './types';
import { defaultSelectedRange } from './utils/selectRange';
import { defaultOnCopy } from './utils/copy';
import DefaultContextMenuRender from './DefaultContextMenuRender';
import DefaultEmptyRowsRenderer from './DefaultEmptyRowsRenderer';
import { GridStore } from './store/gridStore';
import { ContextMenuStore } from './store/contextMenu';
import { RowStore } from './store/rowStore';
import { SELECT_COLUMN_KEY, SelectColumn } from './Columns';
import { message } from 'antd';
import { isEmpty } from 'lodash';
import { Filters } from '.';
import { formatMessage } from './locales';
import useRowContext, { ILazyStoreValue } from './hooks/useRowContext';
import CustomScrollbar from './CustomScrollbar';
import { useSelectScroll } from './hooks/useSelectScroll';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const body = globalThis.document?.body;
const headerRowsCount = 1;

const C_KEY = 67;
const V_KEY = 86;

export const FILTER_NULL_KEY = '(null)';
export const FILTER_EMPTY_KEY = '(empty)';
type OpTagType = 'add' | 'modmifined' | 'delete';

interface UseFilterArgs<R> {
  rowKeyName: string;
  enableFilterRow: boolean;
  columns: DataGridProps<R>['initialColumns'];
  rows: DataGridProps<R>['initialRows'];
  updateRows: DataGridProps<R>['initialRows'];
  tag?: OpTagType;
  preFilterOptions?: FilterOptions;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getRowFilterOptions = <R extends Record<string, any>>(options: UseFilterArgs<R>) => {
  const {
    tag = 'add',
    rowKeyName,
    enableFilterRow,
    columns,
    rows,
    updateRows,
    preFilterOptions = {},
  } = options;
  let filterColumns: DataGridProps<R>['initialColumns'] = [];
  if (!enableFilterRow) {
    filterColumns = [];
  } else {
    filterColumns = columns.filter(({ filterable = true }) => filterable);
  }
  if (!filterColumns.length) {
    return null;
  }
  const nextFilterOptions: FilterOptions = { ...preFilterOptions };
  if (isEmpty(preFilterOptions)) {
    filterColumns.forEach((c) => {
      nextFilterOptions[c.key] = new Set<unknown>();
    });
  }
  const newFilterColumns = filterColumns?.filter((column) => !nextFilterOptions[column.key]);
  if (newFilterColumns?.length) {
    newFilterColumns.forEach((c) => {
      nextFilterOptions[c.key] = new Set<unknown>();
    });
    rows.forEach((row: R) => {
      newFilterColumns.forEach((c) => {
        const key = c.key;
        const v = row[key as keyof R];
        if (v === null) {
          nextFilterOptions[key].add(FILTER_NULL_KEY);
        } else if (typeof v === 'string' && !v.trim()) {
          nextFilterOptions[key].add(FILTER_EMPTY_KEY);
        } else if (typeof v !== 'undefined') {
          nextFilterOptions[key].add(v);
        }
      });
    });
  }

  updateRows.forEach((row: R) => {
    filterColumns.forEach((c) => {
      const key = c.key;
      const v = row[key as keyof R];
      if (v === null) {
        // 我们把null当作字符串处理，这样可以让用户输入null来搜索，也可以通过输入null来筛选
        nextFilterOptions[key].add(FILTER_NULL_KEY);
      } else if (typeof v === 'string' && !v.trim()) {
        // 这里是空格，需要都统计为一个空格
        nextFilterOptions[key].add(FILTER_EMPTY_KEY);
      } else if (typeof v !== 'undefined') {
        if (['delete', 'modmifined'].includes(tag)) {
          const preRow = rows?.find(
            (item) => item?.[rowKeyName as keyof R] === row?.[rowKeyName as keyof R],
          );
          const index = rows?.findIndex(
            (item) => item?.[key as keyof R] === preRow?.[key as keyof R],
          );
          const lastIndex = rows?.findLastIndex(
            (item) => item?.[key as keyof R] === preRow?.[key as keyof R],
          );
          const isOnlyOne = index === lastIndex && index !== -1;
          if (tag === 'delete') {
            if (isOnlyOne) {
              nextFilterOptions[key].delete(v);
            }
          } else {
            if (isOnlyOne) {
              nextFilterOptions[key].delete(preRow?.[key as keyof R]);
            }
            nextFilterOptions[key].add(v);
          }
        } else {
          nextFilterOptions[key].add(v);
        }
      }
    });
  });
  return nextFilterOptions;
};
/**
 * Main API Component to render a data grid of rows and columns
 *
 * @example
 *
 * <DataGrid columns={columns} rows={rows} />
 */
function DataGrid<R extends RowChangeType<R>, SR>(
  {
    initialColumns,
    initialRows,
    rowKeyName = '__id',
    onRowsChange,
    onSelectChange,
    rowRenderer: RowRenderer = Row,
    emptyRowsRenderer: EmptyRowsRenderer = DefaultEmptyRowsRenderer,
    onCopy = defaultOnCopy,
    onFill,
    defaultColumnOptions,
    contextMenuRender = DefaultContextMenuRender,
    getContextMenuConfig,
    editorPortalTarget = body,
    className,
    style,
    rowClass,
    pasteFormatter,
    options,
    onSort,
  }: DataGridProps<R, SR>,
  ref: React.Ref<DataGridRef<R>>,
) {
  const { rowHeight = 35 } = options ?? {};
  const {
    theme = 'white',
    headerRowHeight = rowHeight,
    headerFiltersHeight = 45,
    summaryRowHeight = rowHeight,
    enableFilterRow = false,
    enableSortRow = false,
    enableSelect = true,
    enableVirtualization = true,
    enableRowRecord = false,
    enableRowTools = true,
    enableFrozenRow = false,
    enableAddRow = true,
    enableFlushDelete = false,
    enableBuiltInSort = true,
    searchKey = '',
    aria,
    cellNavigationMode = 'NONE',
  } = options ?? {};
  /**
   * states
   */
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  // 列宽，初始值为 空 map，当列 resize 时更新
  const [columnWidths, setColumnWidths] = useState<ReadonlyMap<string, number>>(() => new Map());
  const [isDragging, setDragging] = useState(false);
  const [draggedOverRowIdx, setOverRowIdx] = useState<number | undefined>(undefined);
  const [, forceUpdate] = useState('');
  const [showMenuRowIdx, _setShowMenuRowIdx] = useState<number>(-1);
  const [topRowSet, _changeTopRowSet] = useState(new Set<React.Key>());
  const [rawRows, setRows] = useState<R[]>(initialRows);
  const [rawColumns, setColumns] = useState<Column<R, SR>[]>(() => {
    return [SelectColumn].concat(initialColumns);
  });
  const [rowFilters, _setRowFilters] = useState<Filters>({});
  const [rowFilterOptions, setRowFilterOptions] = useState<FilterOptions>(() => {
    return getRowFilterOptions({
      enableFilterRow,
      columns: rawColumns,
      rows: rawRows,
      updateRows: rawRows,
      rowKeyName,
    });
  });
  const readonlyRef = useLatest(options?.readonly);

  const rowKeyGetter = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (row: { [key: string]: any }) => {
      return row?.[rowKeyName];
    },
    [rowKeyName],
  );

  /**
   * 引用计数来计算菜单显示和隐藏，可以解决先后顺序问题。
   */
  const countMap = useRef<Map<number, number>>(new Map());
  const setShowMenuRowIdx = useCallback(
    (rowIdx: number, isShow = true) => {
      let count = countMap.current.get(rowIdx) ?? 0;
      if (isShow) {
        count++;
      } else {
        count--;
      }
      if (count > 0) {
        countMap.current.set(rowIdx, count);
      } else if (countMap.current.has(rowIdx)) {
        countMap.current.delete(rowIdx);
      }
      const rowIdxs = [...countMap.current.keys()];
      if (rowIdxs.length === 0) {
        _setShowMenuRowIdx(-1);
      } else {
        _setShowMenuRowIdx(rowIdxs?.[0]);
      }
    },
    [_setShowMenuRowIdx, showMenuRowIdx, countMap],
  );
  /**
   * 存储不需要实施更新组件的state
   */
  const lazyStore = useMemo(() => {
    return new Map<string, unknown>([['isMouseDown', false]]);
  }, []);
  const isMouseDown = lazyStore.get('isMouseDown');
  const setMouseDown = useCallback((v: boolean) => {
    lazyStore.set('isMouseDown', v);
    forceUpdate(`${Math.random()}`);
  }, []);

  // sort
  const { sortInfo, sortRows, handleSort, isExternalSort } = useSorting<R, SR>({
    onSort,
    enableBuiltInSort,
  });

  const innerOnSort = useCallback(
    (columnKey: string, direction: SortDirection) => {
      const column = rawColumns.find((col) => col.key === columnKey);
      if (isExternalSort(column, onSort)) {
        onSort?.(columnKey, direction);
      }
      handleSort(columnKey, direction);
      focusSinkRef.current?.focus();
    },
    [handleSort, rawColumns],
  );

  /**
   * computed values
   */
  const [gridRef, gridWidth, gridHeight] = useGridDimensions();
  const summaryRows: SR[] = useMemo(
    () =>
      rawRows.filter((row) => {
        return topRowSet.has(rowKeyGetter(row));
      }) as unknown as SR[],
    [rawRows, topRowSet, rowKeyGetter],
  );

  const { deletedRowKeys, newRowKeys, modifiedRowKeys } = useMemo(() => {
    const deletedRowKeys = new Set();
    const newRowKeys = new Set();
    const modifiedRowKeys = new Set();
    for (let index = 0; index < rawRows.length; index++) {
      const row = rawRows[index];
      const key = rowKeyGetter(row);
      if (row._created) {
        newRowKeys.add(key);
      } else if (row._originRow) {
        modifiedRowKeys.add(key);
      } else if (row._deleted) {
        deletedRowKeys.add(key);
      }
    }
    return { deletedRowKeys, newRowKeys, modifiedRowKeys };
  }, [rawRows, rowKeyGetter]);

  const summaryRowsCount = summaryRows?.length ?? 0;
  const totalHeaderHeight = headerRowHeight + summaryRows.length * summaryRowHeight;
  const clientHeight = gridHeight - totalHeaderHeight;
  const { isMouseInElement, mouseLeaveEvent } = useMouse(gridRef, !!isMouseDown);

  const changeTopRowSet = useCallback(
    (newSet: Set<React.Key>) => {
      if (topRowSet.size > newSet.size) {
        /**
         * 取消冻结不做限制
         */
        _changeTopRowSet(newSet);
        return;
      }
      if ((newSet.size + 2) * summaryRowHeight > gridHeight - totalHeaderHeight) {
        message.warning(formatMessage('DataGrid.changeTopRowSet.tooMuchFrozen'));
      } else {
        _changeTopRowSet(newSet);
      }
    },
    [gridHeight, _changeTopRowSet, topRowSet],
  );

  const {
    columns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    layoutCssVars,
    columnMetrics,
    totalColumnWidth,
    lastFrozenColumnIndex,
    totalFrozenColumnWidth,
    colVisibleStartIdx = 0,
    colVisibleEndIdx = 0,
  } = useCalculatedColumns({
    rowsCount: rawRows.length,
    rawColumns,
    columnWidths,
    scrollLeft,
    viewportWidth: gridWidth,
    defaultColumnOptions,
    enableVirtualization,
    readonly: readonlyRef.current,
  });

  const {
    rowOverscanStartIdx,
    rowOverscanEndIdx,
    rowVisibleStartIdx = 0,
    rowVisibleEndIdx = 0,
    rows,
    rowsCount,
  } = useViewportRows({
    rawRows,
    rowHeight,
    clientHeight,
    scrollTop,
    enableVirtualization,
    sortInfo,
    rawColumns,
    rowFilters,
    searchKey,
    sortRows: (rows) => sortRows(rows, rawColumns),
  });

  const minColIdx = 0;

  lazyStore.set('gridWidth', gridWidth);
  lazyStore.set('gridHeight', gridHeight);
  lazyStore.set('gridRef', gridRef);

  const isCellWithinRange = useCallback(
    ({ columnIdx, rowIdx, endRowIdx, endColumnIdx }: IRange): boolean => {
      return (
        rowIdx >= 0 &&
        rowIdx < rawRows.length &&
        columnIdx >= minColIdx &&
        columnIdx < columns.length &&
        endColumnIdx >= 0 &&
        endRowIdx < rawRows.length &&
        endColumnIdx >= minColIdx &&
        endColumnIdx < columns.length
      );
    },
    [columns, rawRows],
  );

  const viewportColumns = useViewportColumns({
    columns,
    colOverscanStartIdx,
    colOverscanEndIdx,
  });
  const { isSelectedRangeEditable, selectedRange, selectedRows, selectedColumns, handleSelect } =
    useSelection(rows, columns, isCellWithinRange, rowKeyGetter, onSelectChange);
  const setRowFilters = useCallback(
    (value: React.SetStateAction<Filters>) => {
      _setRowFilters(value);
    },
    [_setRowFilters, handleSelect],
  );
  /**
   * refs
   */
  const focusSinkRef = useRef<HTMLDivElement>(null);
  const prevSelectedPosition = useRef(selectedRange);
  const prevSelectedRows = useRef(selectedRows);
  const prevSelectedColumn = useRef(selectedColumns);
  const latestDraggedOverRowIdx = useRef(draggedOverRowIdx);
  const isCellFocusable = useRef(false);
  const isWheelingRef = useRef(false);
  const { contextMenuElement, handleContextMenu, handleResetContext } = useRowContext({
    setShowMenuRowIdx,
    getContextMenuConfig,
    ContextMenuRender: contextMenuRender,
    onGridSelect: handleSelect,
    viewportColumns,
    lazyStore: lazyStore as Map<string, ILazyStoreValue>,
    selectedRange,
    selectedRows,
    selectedColumns,
    rows,
    rowKeyName,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowKeyGetter: rowKeyGetter as (row: { [key: string]: any }) => string,
    scrollTop,
    headerRowsCount,
  });
  const selectCellAndCommitEdit = useCallback(
    (position: Position, isShiftClick = false, enableEditor = false) => {
      const { columnIdx, rowIdx } = position;
      handleSelect({ rowIdx, columnIdx, isUpdateIndex: true, isShiftClick, enableEditor });
      commitEditorChanges();
    },
    [handleSelect, commitEditorChanges],
  );

  /**
   * The identity of the wrapper function is stable so it won't break memoization
   */
  const handleFormatterRowChangeLatest = useLatestFunc(updateSingleRow);

  const onRowReorder = useCallback(
    (sourceIndex, targetIndex) => {
      const newRows = [...rows];
      newRows.splice(targetIndex, 0, newRows.splice(sourceIndex, 1)[0]);
      setRows(newRows);
      onRowsChange?.(newRows);
    },
    [rows, rowKeyGetter],
  );

  const onColumnReorder = useCallback(
    (sourceKey, targetColumnKey) => {
      const newColumns = [...rawColumns];
      const srcIdx = newColumns.findIndex((r) => r.key === sourceKey);
      const targetIdx = newColumns.findIndex((r) => r.key === targetColumnKey);
      newColumns.splice(
        targetIdx > srcIdx ? targetIdx - 1 : targetIdx,
        0,
        newColumns.splice(srcIdx, 1)[0],
      );
      setColumns(newColumns);
    },
    [rawColumns],
  );

  const _setRows = (rows: R[]) => {
    const rowFilterOptions = getRowFilterOptions({
      enableFilterRow,
      columns: rawColumns,
      rows,
      updateRows: rows,
      rowKeyName,
    });
    setRows(rows);
    setRowFilterOptions(rowFilterOptions);
  };

  const _setColumns = (columns: Column<R, SR>[]) => {
    const hasSelectColumn = columns[0]?.key === SELECT_COLUMN_KEY;
    const _columns = hasSelectColumn ? columns : [SelectColumn].concat(columns);
    const rowFilterOptions = getRowFilterOptions({
      enableFilterRow,
      columns: _columns,
      rows: rawRows,
      updateRows: rawRows,
      rowKeyName,
    });
    handleResetContext();
    setColumns(_columns);
    setRowFilterOptions(rowFilterOptions);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setCellsByRowIndex = (rowIdx: number, cells: Record<string, any>) => {
    updateSingleRow({
      ...rawRows[rowIdx],
      ...cells,
    });
  };

  const getCellsByRowIndex = (rowIdx: number) => {
    return rawRows[rowIdx];
  };

  const refContext: DataGridRef<R> = useMemo(
    () => ({
      element: gridRef.current,
      columns: rawColumns,
      rows,
      selectedRange,
      selectedRows,
      selectedColumns,
      enableFrozenRow,
      rowKeyName,
      setRows: _setRows,
      setColumns: _setColumns,
      addRows: onAddRows,
      deleteRows: onDeleteRow,
      onRowReorder,
      scroll(_idx: number, _rowIdx: number, _force?: boolean) {},
      scrollToColumn(columnIdx: number) {
        scrollToCell({ columnIdx });
      },
      scrollToRow(rowIdx, force?: boolean) {
        scrollToCell({ rowIdx }, force);
      },
      scrollToNextPage() {
        gridRef.current?.scrollTo({
          top: scrollTop + clientHeight,
        });
      },
      scrollToPrevPage() {
        gridRef.current?.scrollTo({
          top: Math.max(scrollTop - clientHeight, 0),
        });
      },
      selectCell: selectCellAndCommitEdit,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rowKeyGetter: rowKeyGetter as (row: { [key: string]: any }) => string,
      setCellsByRowIndex,
      getCellsByRowIndex,
    }),
    [
      gridRef,
      scrollTop,
      scrollToCell,
      clientHeight,
      selectCellAndCommitEdit,
      rows,
      columns,
      selectedRows,
      selectedColumns,
      selectedRange,
      rowKeyGetter,
      enableFrozenRow,
      rowVisibleEndIdx,
      rowVisibleStartIdx,
    ],
  );
  const patchRows = useCallback(
    (data: RowsChangeData<R>) => {
      if (readonlyRef.current) {
        return;
      }
      // 先将 patch 应用到 nextRows，再将 patch 合并，并触发 onRowsChange
      let nextRows = [...rawRows];
      let filterTag: OpTagType = 'add';
      let filterRows: DataGridProps<R>['initialRows'] = [];
      if (data.modifiedRows?.length) {
        filterTag = 'modmifined';
        filterRows = data.modifiedRows;
        data.modifiedRows?.forEach((nextRow) => {
          const idx = nextRows.findIndex((item) => rowKeyGetter(item) === rowKeyGetter(nextRow));
          if (idx > -1) {
            nextRows[idx] = nextRow;
            if (nextRows[idx]?._deleted || nextRows[idx]?._created) {
              return;
            }
            nextRows[idx] = {
              ...nextRow,
              modified: true,
            };
            if (!nextRows[idx]._originRow) {
              nextRows[idx] = {
                ...nextRows[idx],
                _originRow: rawRows[idx],
              };
            }
          }
        });
      }
      if (data.deletedRows?.length) {
        filterTag = 'delete';
        filterRows = data.deletedRows;
        data.deletedRows.forEach((row) => {
          const srcIdx = rawRows.findIndex((r) => rowKeyGetter(row) === rowKeyGetter(r));
          if (row._created || enableFlushDelete) {
            //@ts-ignore
            nextRows[srcIdx] = null;
            return;
          }
          nextRows[srcIdx] = {
            ...nextRows[srcIdx],
            _deleted: true,
          };
        });
      }
      if (data.newRows?.length) {
        filterRows = data.newRows;
        let rowIdx = rawRows.length;
        if (selectedRows?.size) {
          const rawRowIndexs = rawRows
            .map((row, idx) => (selectedRows?.has(rowKeyGetter(row)) ? idx : null))
            .filter((a) => a !== null);
          rowIdx = Math.max(...rawRowIndexs);
        }
        nextRows.splice(rowIdx + 1, 0, ...data.newRows);
        setTimeout(() => {
          scrollToCell({ rowIdx: rowIdx + 1 }, false);
        }, 0);
      }
      nextRows = nextRows?.filter(Boolean);
      const nextFilterOptions = getRowFilterOptions({
        enableFilterRow,
        columns: rawColumns,
        rows: rawRows,
        tag: filterTag,
        updateRows: filterRows,
        preFilterOptions: rowFilterOptions,
        rowKeyName,
      });
      setRows(nextRows);
      onRowsChange?.(nextRows);
      setRowFilterOptions(nextFilterOptions);
    },
    [rawRows, rawColumns, rowFilterOptions, selectedRows, readonlyRef, rowKeyGetter],
  );

  const handlePaste = usePaste<R>({ gridContext: refContext, patchRows, pasteFormatter });

  // Cell drag is not supported on a treegrid
  const enableCellDragAndDrop = onFill !== undefined;

  /**
   * effects
   */
  useLayoutEffect(() => {
    const isSelectedCellNoChange =
      selectedRange?.columnIdx === prevSelectedPosition.current?.columnIdx &&
      selectedRange?.rowIdx === prevSelectedPosition.current?.rowIdx &&
      selectedRange?.endColumnIdx === prevSelectedPosition.current?.endColumnIdx &&
      selectedRange?.endRowIdx === prevSelectedPosition.current?.endRowIdx;
    if (
      isSelectedCellNoChange ||
      selectedRange.mode === RangeMode.EDIT ||
      !isCellWithinRange(selectedRange)
    ) {
      if (prevSelectedRows.current !== selectedRows) {
        if (selectedRows?.size) {
          focusSinkRef.current?.focus({ preventScroll: true });
        }
      } else if (prevSelectedColumn.current !== selectedColumns) {
        if (selectedColumns.size) {
          focusSinkRef.current?.focus({ preventScroll: true });
        }
      }
      prevSelectedRows.current = selectedRows;
      prevSelectedColumn.current = selectedColumns;
      return;
    }
    prevSelectedRows.current = selectedRows;
    prevSelectedPosition.current = selectedRange;
    // FIXME 和选中拖拽功能冲突，先注释掉
    // scrollToCell({ idx: selectedPosition.endIdx, rowIdx: selectedPosition.endRowIdx });

    if (isCellFocusable.current) {
      isCellFocusable.current = false;
      return;
    }

    setTimeout(() => {
      /**
       * 防止点击表格之外的组件的时候，焦点被重置
       */
      if (document.activeElement?.contains(focusSinkRef.current)) {
        /**
         * 这里不能马上执行，因为点击时间冒泡的话，会改变焦点，这边要等冒泡结束后再聚焦
         */
        focusSinkRef.current?.focus({ preventScroll: true });
      }
    });
  });

  useLayoutEffect(() => {
    if (gridRef.current) {
      const frame = requestAnimationFrame(() => {
        if (gridRef.current) {
          gridRef.current.scrollTo({
            top: scrollTop,
            behavior: 'auto',
          });
        }
      });
      return () => cancelAnimationFrame(frame);
    }
    return undefined;
  }, [scrollTop]);

  /**
   * 多选模式鼠标松开后的处理
   */
  useEffect(() => {
    function handleMouseUp() {
      if (lazyStore.get('isMouseDown')) {
        setMouseDown(false);
      }
    }
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [lazyStore]);

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (
        focusSinkRef.current === document.activeElement ||
        document.activeElement?.getAttribute('role') === 'gridcell'
      ) {
        const text = e?.clipboardData?.getData('text');
        if (text) {
          handlePaste(text);
        }
      }
    }
    if (!readonlyRef.current) {
      document.addEventListener('paste', onPaste);
    }
    return () => {
      document.removeEventListener('paste', onPaste);
    };
  }, [focusSinkRef, readonlyRef, handlePaste]);

  useEffect(() => {
    handleSelect({ rowIdx: 0, columnIdx: 1, isUpdateIndex: true });
  }, [rowFilters]);

  useImperativeHandle(ref, () => refContext, [refContext]);

  /**
   * callbacks
   */
  const handleColumnResize = useCallback((column: CalculatedColumn<R, SR>, width: number) => {
    setColumnWidths((columnWidths) => {
      const newColumnWidths = new Map(columnWidths);
      newColumnWidths.set(column.key, width);
      return newColumnWidths;
    });
  }, []);

  const setDraggedOverRowIdx = useCallback((rowIdx?: number) => {
    setOverRowIdx(rowIdx);
    latestDraggedOverRowIdx.current = rowIdx;
  }, []);

  function onGridFocus() {
    if (!isCellWithinRange(selectedRange) && !selectedRows?.size && !selectedColumns.size) {
      // Tabbing into the grid should initiate keyboard navigation
      const initialPosition: ISelectRange = {
        columnIdx: 1,
        rowIdx: 0,
        mode: RangeMode.SELECT,
        endColumnIdx: 1,
        endRowIdx: 0,
      };
      if (isCellWithinRange(initialPosition)) {
        const { columnIdx, rowIdx } = initialPosition;
        handleSelect({
          columnIdx,
          rowIdx,
          mode: RangeMode.SELECT, // select 拓展一个 mode 字段，或者单独维护一个 mode 字段
        });
      }
    } else {
      // otherwise if we already have a selected cell, we should scroll back to it when focusing the grid
      // scrollToCell(selectedPosition);
    }
  }

  function handleCopy(event: React.KeyboardEvent<HTMLDivElement>) {
    const { keyCode } = event;
    const target = event.target as HTMLElement;
    if (!target || !gridRef.current?.contains(target)) {
      return;
    }
    if (
      isCtrlKeyHeldDown(event) &&
      (isCellWithinRange(selectedRange) ||
        (selectedRows?.size || -1) > 0 ||
        !!selectedColumns.size) &&
      selectedRange.mode === RangeMode.SELECT
    ) {
      // event.key may differ by keyboard input language, so we use event.keyCode instead
      // event.nativeEvent.code cannot be used either as it would break copy/paste for the DVORAK layout
      if (keyCode === C_KEY) {
        onCopy?.(refContext);
        return;
      }
      if (keyCode === V_KEY) {
        return;
      }
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (!target || !gridRef.current?.contains(target)) {
      return;
    }
    if (
      isCtrlKeyHeldDown(event) &&
      (isCellWithinRange(selectedRange) ||
        (selectedRows?.size || -1) > 0 ||
        !!selectedColumns.size) &&
      selectedRange.mode === RangeMode.SELECT
    ) {
      if ([C_KEY, V_KEY].includes(event?.keyCode)) {
        return;
      }
    }

    switch (event.key) {
      case 'Escape':
        closeEditor();
        return;
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'Tab':
      case 'Home':
      case 'End':
      case 'PageUp':
      case 'PageDown':
        navigate(event);
        break;
      default:
        handleCellInput(event);
        break;
    }
  }

  function handleFocus() {
    isCellFocusable.current = true;
  }

  const updateScrollState = useCallback(
    throttle((scrollTop, scrollLeft) => {
      setScrollTop(scrollTop);
      setScrollLeft(scrollLeft);
    }, 100),
    [setScrollTop, setScrollLeft],
  );

  const handleScroll = function handleScroll(event: React.UIEvent<HTMLDivElement>) {
    if (isWheelingRef.current) {
      return;
    }
    updateScrollState(event?.currentTarget?.scrollTop, event?.currentTarget?.scrollLeft);
  };

  function handleCustomScroll(newScrollTop: number) {
    setScrollTop(newScrollTop);
  }

  function onAddRows(rows: R[]) {
    const newRows = rows?.map((item) => ({
      ...item,
      _created: true,
    }));
    patchRows({
      newRows,
    });
  }

  function onDeleteRow() {
    let selectedKeys: ReadonlySet<React.Key> = new Set();
    if (selectedRows?.size) {
      selectedKeys = selectedRows;
    } else if (selectedRange.rowIdx !== -1) {
      const maxIdx = Math.max(selectedRange.rowIdx, selectedRange.endRowIdx);
      const minIdx = Math.min(selectedRange.rowIdx, selectedRange.endRowIdx);
      selectedKeys = new Set(rows.slice(minIdx, maxIdx + 1).map((row) => rowKeyGetter(row)));
    }
    if (!selectedKeys?.size) {
      return;
    }
    const deletedRows = rows.filter((row) => selectedKeys?.has(rowKeyGetter(row)));
    patchRows({
      deletedRows,
    });
  }

  function updateSingleRow(row: R) {
    const idx = rawRows.findIndex((r) => {
      return rowKeyGetter(row) === rowKeyGetter(r);
    });
    if (idx !== -1) {
      patchRows({
        modifiedRows: [row],
        indexes: [idx],
        column: columns[selectedRange.columnIdx],
      });
    }
  }

  function commitEditorChanges() {
    const row = rows[selectedRange.rowIdx];
    if (
      columns[selectedRange.columnIdx]?.editor === undefined ||
      selectedRange.mode === RangeMode.SELECT ||
      row === row._originRow
    ) {
      return;
    }
    updateSingleRow(row);
  }

  function handleCellInput(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!isCellWithinRange(selectedRange)) {
      return;
    }
    const { key } = event;
    const column = columns[selectedRange.columnIdx];

    if (selectedRange.mode === RangeMode.EDIT) {
      if (key === 'Enter' && !event.shiftKey) {
        // Custom editors can listen for the event and stop propagation to prevent commit
        commitEditorChanges();
        closeEditor();
      }
      return;
    }

    column.editorOptions?.onCellKeyDown?.(event);
    if (event.isDefaultPrevented()) {
      return;
    }

    if (isSelectedRangeEditable && isDefaultCellInput(event) && !readonlyRef.current) {
      if (key === 'Enter') {
        event.preventDefault();
      }
      handleSelect({
        ...selectedRange,
        isUpdateIndex: true,
        mode: RangeMode.EDIT,
      });
    }
  }

  function handleDragEnd() {
    const overRowIdx = latestDraggedOverRowIdx.current;
    if (overRowIdx === undefined || !onFill || !onRowsChange) {
      return;
    }

    const { columnIdx, rowIdx } = selectedRange;
    const sourceRow = rawRows[rowIdx];
    const startRowIndex = rowIdx < overRowIdx ? rowIdx + 1 : overRowIdx;
    const endRowIndex = rowIdx < overRowIdx ? overRowIdx + 1 : rowIdx;
    const targetRows = rawRows.slice(startRowIndex, endRowIndex);
    const column = columns[columnIdx];
    const updatedTargetRows = onFill({ columnKey: column.key, sourceRow, targetRows });
    const updatedRows = [];
    const indexes: number[] = [];

    for (let i = startRowIndex; i < endRowIndex; i++) {
      const nextRow = updatedTargetRows[i - startRowIndex];
      updatedRows.push(nextRow);
      indexes.push(i);
    }

    patchRows({ modifiedRows: updatedRows });
    setDraggedOverRowIdx(undefined);
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (event.buttons !== 1) {
      return;
    }
    setDragging(true);
    window.addEventListener('mouseover', onMouseOver);
    window.addEventListener('mouseup', onMouseUp);

    function onMouseOver(event: MouseEvent) {
      // Trigger onMouseup in edge cases where we release the mouse button but `mouseup` isn't triggered,
      // for example when releasing the mouse button outside the iframe the grid is rendered in.
      // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons
      if (event.buttons !== 1) {
        onMouseUp();
      }
    }

    function onMouseUp() {
      window.removeEventListener('mouseover', onMouseOver);
      window.removeEventListener('mouseup', onMouseUp);
      setDragging(false);
      handleDragEnd();
    }
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (!onFill || !onRowsChange) {
      return;
    }

    const { columnIdx, rowIdx } = selectedRange;
    const sourceRow = rawRows[rowIdx];
    const targetRows = rawRows.slice(rowIdx + 1);
    const column = columns[columnIdx];
    const updatedTargetRows = onFill({ columnKey: column.key, sourceRow, targetRows });
    const updatedRows = [];
    const indexes: number[] = [];

    for (let i = rowIdx + 1; i < rawRows.length; i++) {
      const nextRow = updatedTargetRows[i - rowIdx - 1];
      updatedRows.push(nextRow);
      indexes.push(i);
    }

    patchRows({ modifiedRows: updatedRows });
  }

  function handleEditorRowChange(row: Readonly<R>, commitChanges?: boolean) {
    if (selectedRange.mode === RangeMode.SELECT) {
      return;
    }
    if (commitChanges) {
      if (row !== rows[selectedRange.rowIdx]) {
        updateSingleRow(row);
      }
      if (
        gridRef.current?.contains(document.activeElement) ||
        document.querySelector('.rdg-editor-archor')?.contains(document.activeElement)
      ) {
        /**
         * 编辑完成的时候，假如点击的是表格内部，那么需要把焦点重新聚集回来，避免keydown等事件无法响应
         */
        focusSinkRef.current?.focus();
      }
      closeEditor();
    } else {
      updateSingleRow(row);
    }
  }

  function handleOnClose(commitChanges?: boolean) {
    if (commitChanges) {
      commitEditorChanges();
    }
    closeEditor();
  }

  /**
   * utils
   */
  function closeEditor() {
    if (selectedRange.mode === RangeMode.SELECT) {
      return;
    }
    handleSelect({ ...selectedRange, mode: RangeMode.SELECT, isUpdateIndex: true });
  }

  function scrollToCell({ columnIdx, rowIdx }: Partial<Position>, force?: boolean): void {
    const { current } = gridRef;
    if (!current) {
      return;
    }

    if (typeof columnIdx === 'number' && columnIdx > lastFrozenColumnIndex) {
      columnIdx = Math.min(columns.length - 1, columnIdx || 0);
      rowIdx ??= selectedRange.rowIdx;
      if (!isCellWithinRange({ rowIdx, columnIdx, endColumnIdx: columnIdx, endRowIdx: rowIdx })) {
        return;
      }
      const { clientWidth } = current;
      const column = columns[columnIdx];
      const { left, width } = columnMetrics.get(column) as ColumnMetric;
      const right = left + width;
      const isCellAtLeftBoundary = left < scrollLeft + totalFrozenColumnWidth;
      const isCellAtRightBoundary = right > clientWidth + scrollLeft;
      let nextScrollLeft = null;
      if (isCellAtLeftBoundary) {
        nextScrollLeft = left - totalFrozenColumnWidth;
      } else if (isCellAtRightBoundary) {
        nextScrollLeft = right - clientWidth;
      }
      if (!isNil(nextScrollLeft)) {
        current.scrollTo({
          left: nextScrollLeft,
          behavior: 'auto',
        });
      }
    }

    if (typeof rowIdx === 'number') {
      rowIdx = Math.min(rawRows.length, rowIdx || 0);
      const nextScrollTop = rowIdx * rowHeight;
      if (nextScrollTop < scrollTop || force) {
        current.scrollTo({
          top: nextScrollTop,
          behavior: 'auto',
        });
      } else if (nextScrollTop + rowHeight > scrollTop + clientHeight) {
        // at bottom boundary, scroll the next row's top to the bottom of the viewport
        current.scrollTo({
          top: nextScrollTop + rowHeight - clientHeight,
          behavior: 'auto',
        });
      }
    }
  }

  function getNextPosition(key: string, ctrlKey: boolean, shiftKey: boolean): Position {
    const { columnIdx, rowIdx } = selectedRange;
    const isRowSelected = isCellWithinRange(selectedRange) && columnIdx === -1;

    switch (key) {
      case 'ArrowUp':
        return { columnIdx, rowIdx: rowIdx - 1 };
      case 'ArrowDown':
        return { columnIdx, rowIdx: rowIdx + 1 };
      case 'ArrowLeft':
        return { columnIdx: columnIdx - 1, rowIdx };
      case 'ArrowRight':
        return { columnIdx: columnIdx + 1, rowIdx };
      case 'Tab':
        if (selectedRange.columnIdx === -1 && selectedRange.rowIdx === -1) {
          return shiftKey
            ? { columnIdx: columns.length - 1, rowIdx: rows.length - 1 }
            : { columnIdx: 0, rowIdx: 0 };
        }
        return { columnIdx: columnIdx + (shiftKey ? -1 : 1), rowIdx };
      case 'Home':
        // If row is selected then move focus to the first row
        if (isRowSelected) {
          return { columnIdx, rowIdx: 0 };
        }
        return ctrlKey ? { columnIdx: 1, rowIdx: 0 } : { columnIdx: 1, rowIdx };
      case 'End':
        // If row is selected then move focus to the last row.
        if (isRowSelected) {
          return { columnIdx, rowIdx: rows.length - 1 };
        }
        return ctrlKey
          ? { columnIdx: columns.length - 1, rowIdx: rows.length - 1 }
          : { columnIdx: columns.length - 1, rowIdx };
      case 'PageUp':
        return { columnIdx, rowIdx: rowIdx - Math.floor(clientHeight / rowHeight) };
      case 'PageDown':
        return { columnIdx, rowIdx: rowIdx + Math.floor(clientHeight / rowHeight) };
      default:
        return selectedRange;
    }
  }

  function navigate(event: React.KeyboardEvent<HTMLDivElement>) {
    if (selectedRange.mode === RangeMode.EDIT) {
      const onNavigation =
        columns[selectedRange.columnIdx].editorOptions?.onNavigation ?? onEditorNavigation;
      if (!onNavigation(event)) {
        return;
      }
    }
    // Do not allow focus to leave
    event.preventDefault();
    const { key, shiftKey } = event;
    let mode = cellNavigationMode;
    if (key === 'Tab') {
      // If we are in a position to leave the grid, stop editing but stay in that cell
      if (
        canExitGrid({
          shiftKey,
          cellNavigationMode,
          columns,
          rowsCount: rows.length,
          selectedRange,
        })
      ) {
        console.log('tab focus');
        focusSinkRef.current?.focus();
        commitEditorChanges();
        // Allow focus to leave the grid so the next control in the tab order can be focused
        return;
      }

      mode = cellNavigationMode === 'NONE' ? 'CHANGE_ROW' : cellNavigationMode;
    }

    const ctrlKey = isCtrlKeyHeldDown(event);
    const nextPosition = getNextSelectedCellPosition({
      columns,
      rows,
      cellNavigationMode: mode,
      nextPosition: getNextPosition(key, ctrlKey, shiftKey),
    });

    selectCellAndCommitEdit(nextPosition);
    setTimeout(() => {
      console.log('timeout focus');
      scrollToCell(nextPosition);
      focusSinkRef.current?.focus();
    });
  }

  function getDraggedOverCellIdx(currentRowIdx: number): number | undefined {
    if (draggedOverRowIdx === undefined) {
      return;
    }
    const { rowIdx } = selectedRange;

    const isDraggedOver =
      rowIdx < draggedOverRowIdx
        ? rowIdx < currentRowIdx && currentRowIdx <= draggedOverRowIdx
        : rowIdx > currentRowIdx && currentRowIdx >= draggedOverRowIdx;
    return isDraggedOver ? selectedRange.columnIdx : undefined;
  }

  function getSelectedCellProps(rowIdx: number): SelectedCellProps | EditCellProps<R> | undefined {
    if (selectedRange.rowIdx !== rowIdx) {
      return;
    }

    if (selectedRange.mode === RangeMode.EDIT) {
      return {
        mode: RangeMode.EDIT,
        idx: selectedRange.columnIdx,
        onKeyDown: handleKeyDown,
        editorProps: {
          editorPortalTarget,
          rowHeight,
          onRowChange: handleEditorRowChange,
          onClose: handleOnClose,
        },
      };
    }

    return {
      mode: RangeMode.SELECT,
      idx: selectedRange.columnIdx,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      dragHandleProps:
        enableCellDragAndDrop && isSelectedRangeEditable
          ? { onMouseDown: handleMouseDown, onDoubleClick: handleDoubleClick }
          : undefined,
    };
  }

  useSelectScroll(
    isMouseInElement,
    mouseLeaveEvent,
    !!isMouseDown,
    gridRef,
    selectedRange,
    colVisibleStartIdx,
    colVisibleEndIdx,
    gridWidth,
    gridHeight,
    handleSelect,
    refContext,
    rowVisibleStartIdx,
    rowVisibleEndIdx,
    rows,
    columns,
    scrollToCell,
  );

  const getModifiedColumns = useCallback(
    (row: R) => {
      const key = rowKeyGetter(row);
      if (!modifiedRowKeys?.has(key)) {
        return [];
      }
      const modifiedColumns: string[] = [];
      Object.keys(row).forEach((columnKey) => {
        // @ts-ignore
        if (row[columnKey] !== row?._originRow?.[columnKey]) {
          modifiedColumns.push(columnKey);
        }
      });
      return modifiedColumns;
    },
    [modifiedRowKeys, rowKeyGetter],
  );

  function getViewportRows() {
    const rowElements = [];
    const mainKey = [...(selectedRows || [])].pop();
    for (let rowIdx = rowOverscanStartIdx; rowIdx <= rowOverscanEndIdx; rowIdx++) {
      const row = rows[rowIdx];
      const top = rowIdx * rowHeight + totalHeaderHeight;
      const key = rowKeyGetter(row);
      let isRowSelected = false;
      isRowSelected = selectedRows?.has(key) ?? false;
      rowElements.push(
        <RowRenderer
          aria-rowindex={headerRowsCount + rowIdx + 1} // aria-rowindex is 1 based
          aria-selected={enableSelect ? isRowSelected : undefined}
          key={key}
          rowIdx={rowIdx}
          isLastRow={rowIdx === rows.length - 1}
          row={row}
          totalColumnWidth={totalColumnWidth}
          selectedColumns={selectedColumns}
          getModifiedColumns={getModifiedColumns}
          isAdd={newRowKeys?.has(key)}
          isDeleted={deletedRowKeys?.has(key)}
          enableRowRecord={enableRowRecord}
          lazyStore={lazyStore}
          setMouseDown={setMouseDown}
          viewportColumns={viewportColumns}
          columnMetrics={columnMetrics}
          isRowSelected={isRowSelected}
          isMainSelectedRow={mainKey === key}
          rowClass={rowClass}
          top={top}
          draggedOverCellIdx={getDraggedOverCellIdx(rowIdx)}
          setDraggedOverRowIdx={isDragging ? setDraggedOverRowIdx : undefined}
          lastFrozenColumnIndex={lastFrozenColumnIndex}
          selectedCellProps={getSelectedCellProps(rowIdx)}
          selectedRange={selectedRange}
          onRowChange={handleFormatterRowChangeLatest}
          onRowReorder={onRowReorder}
          onGridSelect={handleSelect}
        />,
      );
    }
    return rowElements;
  }

  useEffect(() => {
    // Reset the positions if the current values are no longer valid. This can happen if a column or row is removed
    if (selectedRange.columnIdx >= columns.length || selectedRange.rowIdx >= rows.length) {
      handleSelect(defaultSelectedRange);
      setDraggedOverRowIdx(undefined);
    }
  }, [selectedRange, columns.length, rows.length, handleSelect, setDraggedOverRowIdx]);

  const resetIsWheeling = useMemo(
    () =>
      debounce(() => {
        isWheelingRef.current = false;
      }, 150),
    [],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      const { deltaX, deltaY } = event;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        isWheelingRef.current = true;
        event.preventDefault();
        const scrollHeight = rowsCount * rowHeight;
        const maxScrollTop = scrollHeight - clientHeight;
        const newScrollTop = Math.max(0, Math.min(scrollTop + deltaY, maxScrollTop));
        handleCustomScroll(newScrollTop);
        resetIsWheeling();
      }
    },
    [rowsCount, rowHeight, clientHeight, scrollTop, handleCustomScroll, resetIsWheeling],
  );

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (grid) {
      grid.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        grid.removeEventListener('wheel', handleWheel);
      };
    }
    return undefined;
  }, [handleWheel]);

  return (
    <GridStore.Provider
      value={{
        gridRef: refContext,
      }}
    >
      <ContextMenuStore.Provider
        value={{
          getContextMenuConfig,
          ContextMenuRender: contextMenuRender,
          onAddRows,
          onDeleteRow,
          onRowReorder,
          enableAddAction: enableAddRow,
          enableRowRecord,
          enableRowTools,
          readonly: readonlyRef.current,
        }}
      >
        <RowStore.Provider
          value={{
            showMenuRowIdx,
            setShowMenuRowIdx,
            topRowSet,
            changeTopRowSet,
          }}
        >
          <div style={{ position: 'relative', height: '100%', ...style }} className={className}>
            <div
              role="grid"
              {...aria}
              onKeyDown={handleCopy}
              aria-multiselectable={enableSelect ? true : undefined}
              aria-colcount={columns.length}
              aria-rowcount={headerRowsCount + rowsCount + summaryRowsCount}
              tabIndex={0}
              className={clsx(rootClassname, {
                [viewportDraggingClassname]: isDragging,
                'rdg-dark': theme === 'dark',
              })}
              style={
                {
                  height: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  '--header-row-height': `${headerRowHeight}px`,
                  '--filter-row-height': `${headerFiltersHeight}px`,
                  '--row-width': `${totalColumnWidth}px`,
                  '--row-height': `${rowHeight}px`,
                  '--summary-row-height': `${summaryRowHeight}px`,
                  ...layoutCssVars,
                } as unknown as React.CSSProperties
              }
              ref={gridRef}
              onScroll={handleScroll}
              onContextMenu={handleContextMenu}
            >
              <HeaderRow<R, SR>
                selectedRange={selectedRange}
                selectedColumns={selectedColumns}
                onGridSelect={handleSelect}
                columns={viewportColumns}
                onColumnResize={handleColumnResize}
                rowFilters={rowFilters}
                rowFilterOptions={rowFilterOptions}
                onFiltersChange={setRowFilters}
                allRowsSelected={selectedRows?.size === rows.length}
                onColumnReorder={onColumnReorder}
                enableSortRow={enableSortRow}
                sortInfo={sortInfo}
                onSort={innerOnSort}
                lastFrozenColumnIndex={lastFrozenColumnIndex}
              />
              {rows.length === 0 && EmptyRowsRenderer ? (
                <EmptyRowsRenderer />
              ) : (
                <>
                  {summaryRows?.map((row, rowIdx) => (
                    <SummaryRow<R, SR>
                      aria-rowindex={headerRowsCount + rowsCount + rowIdx + 1}
                      key={rowIdx}
                      rowIdx={rowIdx}
                      isLastSummaryRow={rowIdx === summaryRows.length - 1}
                      row={{
                        ...row,
                        [SELECT_COLUMN_KEY]:
                          rows.findIndex(
                            (_row) => rowKeyGetter(_row) === String(row[rowKeyName as keyof SR]),
                          ) + 1,
                      }}
                      top={headerRowHeight + summaryRowHeight * rowIdx}
                      viewportColumns={viewportColumns}
                      lastFrozenColumnIndex={lastFrozenColumnIndex}
                    />
                  ))}
                  <div
                    ref={focusSinkRef}
                    tabIndex={0}
                    className={focusSinkClassname}
                    onKeyDown={handleKeyDown}
                    onFocus={onGridFocus}
                  />
                  <div
                    className="rdg-pseudo-container"
                    style={{ height: Math.max(rows.length * rowHeight, clientHeight - 2) }}
                  />
                  {getViewportRows()}
                </>
              )}
              {contextMenuElement}
            </div>
            <CustomScrollbar
              scrollTop={scrollTop}
              clientHeight={clientHeight}
              scrollHeight={rows.length * rowHeight}
              onScroll={handleCustomScroll}
              headerHeight={totalHeaderHeight}
              isSelectDragging={!!isMouseDown}
              onwheel={(e) => handleWheel(e.nativeEvent)}
            />
            <div
              className="rdg-editor-archor"
              style={{
                position: 'absolute',
                top: -scrollTop,
                left: -scrollLeft,
              }}
            ></div>
          </div>
        </RowStore.Provider>
      </ContextMenuStore.Provider>
    </GridStore.Provider>
  );
}

export default forwardRef(DataGrid) as <R, SR = unknown>(
  props: DataGridProps<R, SR> & RefAttributes<DataGridRef<R>>,
) => JSX.Element;
